import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FieldRow } from '@/components/common/FieldRow';
import { GRADE_OPTIONS, GENDER_OPTIONS } from '../data';
import { cn } from '@/lib/utils';

const COURSE_ONLY_FROM_NUMERIC_GRADE = 7;

function isElementaryGradeOption(option) {
  if (option.value === 'college') return false;
  const numeric = parseInt(option.value, 10);
  return Number.isFinite(numeric) && numeric < COURSE_ONLY_FROM_NUMERIC_GRADE;
}

function renderGradeOptionContent(option) {
  if (isElementaryGradeOption(option)) {
    return (
      <span className="text-xs sm:text-sm">
        {option.label}{' '}
        <span className="text-slate-500">({option.subject})</span>
      </span>
    );
  }
  return <span className="text-xs sm:text-sm">{option.subject}</span>;
}

function renderSelectedGrade(value) {
  const option = GRADE_OPTIONS.find((o) => o.value === value);
  if (!option) return null;
  if (isElementaryGradeOption(option)) {
    return `${option.label} (${option.subject})`;
  }
  return option.subject;
}

export function PersonFields({ data, onChange, idPrefix, showName = true }) {
  function setField(field, value) {
    onChange({ ...data, [field]: value });
  }

  function setGrade(value) {
    const option = GRADE_OPTIONS.find((o) => o.value === value);
    onChange({
      ...data,
      grade: value,
      subject: option?.subject ?? '',
    });
  }

  return (
    <div className="grid gap-4">
      {showName && (
        <FieldRow id={`${idPrefix}-name`} label="Name" required>
          <Input
            id={`${idPrefix}-name`}
            type="text"
            value={data.name}
            onChange={(e) => setField('name', e.target.value)}
            autoComplete="name"
            className="cursor-text"
          />
        </FieldRow>
      )}

      <FieldRow id={`${idPrefix}-grade`} label="Target Level" required>
        <Select value={data.grade} onValueChange={setGrade}>
<SelectTrigger id={`${idPrefix}-grade`} className="cursor-pointer">
          {data.grade ? (
            <span className="text-xs sm:text-sm">{renderSelectedGrade(data.grade)}</span>
          ) : (
            <SelectValue placeholder="Select level" />
          )}
        </SelectTrigger>
          <SelectContent>
            {GRADE_OPTIONS.map((o) => (
              <SelectItem
                key={o.value}
                value={o.value}
                className="cursor-pointer"
              >
                {renderGradeOptionContent(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow id={`${idPrefix}-gender`} label="Gender" required>
        <div
          role="radiogroup"
          aria-label="Gender"
          className="grid grid-cols-2 gap-3"
        >
          {GENDER_OPTIONS.map((opt) => {
            const active = data.gender === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setField('gender', opt.value)}
                className={cn(
                  'h-14 cursor-pointer rounded-xl border-2 text-base font-bold transition-all',
                  active
                    ? 'border-brand-600 bg-brand-600 text-white shadow-md ring-2 ring-brand-200'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-brand-300 hover:bg-slate-50'
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </FieldRow>
    </div>
  );
}